/**
 * Scenarios for Site A — Kettle Narrows (docs/SCENARIOS.md).
 *
 * A scenario is **config, never code**. Weather keyframes are the honest statement of how much
 * water the scenario delivers: the intermittency noise applied on top has mean 1.0, and every
 * weather-driven event (E01, E02, E20, E26, E27) is authored *here* rather than as a runtime
 * mutation, because the forecaster runs the same pure weather function forward (DECISIONS D1(d)).
 *
 * The keyframes below were calibrated against the engine, not guessed: Quiet October settles at
 * ~420 m³/s, the freshet peaks near 3,200 m³/s, and the atmospheric river peaks near 4,500 m³/s.
 */

import type { ScenarioConfig } from '../engine/types';
import { cascadeDownstream } from './cascade';

const SITE = 'kettle-narrows';

export const handover: ScenarioConfig = {
  id: 's0-handover',
  index: 0,
  name: 'Handover',
  siteId: SITE,
  start: '2026-10-14T06:00',
  durationH: 8,
  teaches: 'Units, gates, level hold, alarms, and reading the cross-section.',
  briefing:
    'Day shift. The pool is at 752.4 m, a metre under the October rule curve, and inflow is quiet ' +
    'at about 420 m³/s. Two units are on at 180 MW each. Night shift left you a clean plant.\n\n' +
    'Work through the handover checklist on the right: start a third unit, take it to a setpoint, ' +
    'move a spillway gate through the two-step confirm, acknowledge an alarm, and put the plant in ' +
    'level hold. A unit will trip about five hours in, which is your alarm.\n\n' +
    'Nothing here will hurt you — penalties are halved and the weather is quiet. Learn where things ' +
    'are, and spend a minute on the cross-section: it is drawn to true vertical scale, so the gap ' +
    'between the water and the crest is the same distance on screen that it is in metres.',
  initial: {
    headwater: 752.4,
    swe: [0, 0, 25],
    baseflow: 380,
    wetness: 0.4,
    unitsOnline: [0, 1],
    initialSetpointMW: 180,
  },
  weather: [
    { h: 0, tempC: 9, precipMmH: 0.04 },
    { h: 8, tempC: 12, precipMmH: 0.03 },
  ],
  priceOverlays: [],
  events: [{ id: 'E04', atH: 5, target: 1 }],
  hazardRate: 0,
  forecastBias: 0,
  forecastBiasDecayH: 0,
  objectives: [
    { id: 'checklist', text: 'Complete all five handover checklist items', kind: 'checklist-complete', value: 5 },
    { id: 'alarms', text: 'No High alarm left unacknowledged for ten minutes', kind: 'no-unacked-high', value: 0 },
  ],
  debriefHints: [
    'The cross-section is drawn to true vertical scale for water elevations. The gap between the surface and the crest is your freeboard, and it is the same distance on screen that it is in metres.',
  ],
  gradeThresholds: [0, -1e6, -5e6, -1e7],
  tutorial: true,
};

export const quietOctober: ScenarioConfig = {
  id: 's1-quiet-october',
  index: 1,
  name: 'Quiet October',
  siteId: SITE,
  start: '2026-10-05T00:00',
  durationH: 168,
  teaches: 'Price-following dispatch inside the rule curve; water value; the reserve trade.',
  briefing:
    'A week of nothing much. Inflow is decaying through 420 m³/s, the pool sits at 753.0 m against ' +
    'a rule curve of 753, and the spillway will stay shut all week.\n\n' +
    'Every dollar this week is in dispatch. Four units, an hourly price, a 100 MW spinning-reserve ' +
    'obligation that pays $6/MW·h when you carry it and costs $60/MW·h when you do not, and a ' +
    'water value that tells you whether the water in front of you is worth more now or in ' +
    'February. Two price spikes and one negative-price window are already in the day-ahead.',
  initial: {
    headwater: 753.0,
    swe: [0, 0, 20],
    baseflow: 380,
    wetness: 0.4,
    unitsOnline: [0, 1],
    initialSetpointMW: 160,
  },
  weather: [
    { h: 0, tempC: 9, precipMmH: 0.04 },
    { h: 72, tempC: 7, precipMmH: 0.07 },
    { h: 168, tempC: 6, precipMmH: 0.05 },
  ],
  priceOverlays: [
    { fromH: 41, toH: 44, price: 210, label: 'Wednesday evening scarcity' },
    { fromH: 90, toH: 93, price: 340, label: 'Friday evening spike' },
    { fromH: 74, toH: 77, price: -8, label: 'Thursday overnight wind surplus' },
  ],
  events: [
    { id: 'E08', atH: 41, durationH: 3, params: { price: 210 } },
    { id: 'E08', atH: 74, durationH: 3, params: { price: -8 } },
    { id: 'E08', atH: 90, durationH: 3, params: { price: 340 } },
    { id: 'E16', atH: 106, durationH: 6, params: { minFlow: 400, maxFlow: 600 } },
    { id: 'E25', atH: 30, durationH: 0.6, params: { minutes: 32 } },
    { id: 'E07', atH: 64, durationH: 4, params: { bonus: 140 } },
  ],
  hazardRate: 0.5,
  forecastBias: 0,
  forecastBiasDecayH: 0,
  objectives: [
    { id: 'net', text: 'Net at least $4.2 M for the week', kind: 'score-at-least', value: 4_200_000 },
    { id: 'env', text: 'Environmental compliance at 100 %', kind: 'env-compliance-at-least', value: 99.9 },
    { id: 'band', text: 'Finish inside the rule-curve band', kind: 'hw-in-band-at-end', value: 753 },
  ],
  debriefHints: [
    'Reserve is usually worth more than the last 100 MW of energy at night and much less during an evening peak. Two units at 200 MW carry 200 MW of reserve; four units at full load carry none.',
  ],
  gradeThresholds: [5_000_000, 4_200_000, 3_400_000, 2_500_000],
};

export const freeboard: ScenarioConfig = {
  id: 's2-freeboard',
  index: 2,
  name: 'Freeboard',
  siteId: SITE,
  start: '2026-05-20T00:00',
  durationH: 336,
  teaches: 'Refill against flood storage; gate timing; ramp limits.',
  briefing:
    'The name scenario. It is 20 May, the pool is at 728.0 m, and the rule curve climbs from 726 to ' +
    '750 over the next fortnight. There is still 430 mm of snow water in the middle band and 720 in ' +
    'the upper.\n\n' +
    'You have to refill — the water leaving this month is the water you sell in January. You also ' +
    'have to leave room, because a ridge is building and the melt is going to accelerate, then dip, ' +
    'then come back harder. Fill too fast and the second peak spills; fill too slowly and you finish ' +
    'the freshet below the curve with nothing left upstream to catch up with.\n\n' +
    'Gate travel is 53 minutes from shut to full. Decide early.',
  initial: {
    headwater: 728.0,
    swe: [30, 430, 720],
    baseflow: 420,
    wetness: 0.45,
    unitsOnline: [0, 1, 2],
    initialSetpointMW: 200,
  },
  weather: [
    { h: 0, tempC: 7, precipMmH: 0.05 },
    { h: 72, tempC: 9, precipMmH: 0.04 },
    { h: 144, tempC: 10.5, precipMmH: 0.03 },
    { h: 192, tempC: 11.5, precipMmH: 0.03 },
    { h: 216, tempC: 7.5, precipMmH: 0.12 },
    { h: 252, tempC: 9, precipMmH: 0.06 },
    { h: 288, tempC: 12.5, precipMmH: 0.03 },
    { h: 336, tempC: 10, precipMmH: 0.05 },
  ],
  priceOverlays: [],
  events: [
    { id: 'E02', atH: 60, durationH: 120 },
    { id: 'E17', atH: 200, durationH: 72, params: { minPool: 751 } },
    { id: 'E03', atH: 228, durationH: 12, params: { peak: 700, durationH: 12 } },
  ],
  hazardRate: 1.0,
  forecastBias: 0,
  forecastBiasDecayH: 0,
  objectives: [
    { id: 'band', text: 'Finish inside the rule-curve band around 750 m', kind: 'hw-in-band-at-end', value: 750 },
    { id: 'flood', text: 'Keep Ferrisburg below minor flood stage', kind: 'gauge-below', value: 3000 },
    { id: 'safety', text: 'Safety grade B or better', kind: 'safety-at-least', value: 1 },
  ],
  debriefHints: [
    'The correct play starts the gates on the fourth day, not the sixth. The forecast median crosses 2,500 m³/s well before the pool demands action, and 53 minutes of gate travel means the decision is made an hour before the water arrives.',
  ],
  gradeThresholds: [11_000_000, 9_000_000, 7_000_000, 4_000_000],
};

export const atmosphericRiver: ScenarioConfig = {
  id: 's3-atmospheric-river',
  index: 3,
  name: 'Atmospheric River',
  siteId: SITE,
  start: '2026-11-08T12:00',
  durationH: 120,
  teaches: 'Forecast uncertainty, pre-release, flood warning, the TDG waiver.',
  briefing:
    'November, and the pool is where November leaves it: 751.5 m, high, with 2.9 m of freeboard to ' +
    'FSL and 6.5 m to the maximum flood level.\n\n' +
    'The forecast centre has issued a special weather statement. An atmospheric river reaches the ' +
    'basin in about eighteen hours, freezing levels go to 2,400 m, and it will rain on all three ' +
    'snow bands. Their own guidance says confidence is low.\n\n' +
    'It is low. The first issuances under-read the peak by about thirty per cent, and they correct ' +
    'over the following day. Three decisions matter: whether you pre-release before the second ' +
    'issuance, whether the flood warning goes out at least six hours before Ferrisburg breaches, ' +
    'and whether you ask for the TDG waiver before you need it — it takes thirty to ninety minutes ' +
    'to come back.',
  initial: {
    headwater: 751.5,
    swe: [10, 180, 420],
    baseflow: 560,
    wetness: 0.55,
    unitsOnline: [0, 1, 2, 3],
    initialSetpointMW: 200,
  },
  weather: [
    { h: 0, tempC: 4, precipMmH: 0.06 },
    { h: 18, tempC: 9, precipMmH: 0.85 },
    { h: 44, tempC: 10, precipMmH: 1.05 },
    { h: 60, tempC: 6, precipMmH: 0.25 },
    { h: 120, tempC: 4, precipMmH: 0.05 },
  ],
  priceOverlays: [],
  events: [
    { id: 'E01', atH: 1, durationH: 60 },
    { id: 'E19', atH: 6, durationH: 30 },
    // Placed on the routed peak at the gauge, not on the storm peak — the reach lags the dam by
    // about 5.5 h, so a tributary spike at +52 h is what actually arrives with your own water.
    { id: 'E03', atH: 52, durationH: 14, params: { peak: 500, durationH: 14 } },
  ],
  hazardRate: 1.0,
  // The storm is under-read by 30 % at first, correcting over four issuances (24 h).
  forecastBias: -0.3,
  forecastBiasDecayH: 24,
  objectives: [
    { id: 'mfl', text: 'Keep the pool below the maximum flood level', kind: 'hw-below', value: 758 },
    { id: 'flood', text: 'Keep Ferrisburg below moderate flood stage', kind: 'gauge-below', value: 3800 },
    { id: 'safety', text: 'Safety grade B or better', kind: 'safety-at-least', value: 1 },
  ],
  debriefHints: [
    'The band, not the median, is the plan. The 90th percentile crossed 3,500 m³/s a full day before the median did — hedging against it costs a few hours of lower-value generation and buys the whole surcharge pool.',
  ],
  gradeThresholds: [4_000_000, 2_000_000, -1_000_000, -6_000_000],
};

export const trip: ScenarioConfig = {
  id: 's4-trip',
  index: 4,
  name: 'Trip',
  siteId: SITE,
  start: '2026-06-12T00:00',
  durationH: 168,
  teaches: 'Forced outages, hoist failure, and the reserve obligation.',
  briefing:
    'Mid-freshet, inflow steady around 1,800 m³/s, pool at 745 m and rising toward the June curve. ' +
    'Unit 1 is already out for its scheduled outage.\n\n' +
    'Nothing about this week is dramatic. Everything about it is tight: three machines, four gates, ' +
    'a 100 MW reserve obligation, and a rule-curve band you have to stay inside while equipment ' +
    'comes and goes underneath you.',
  initial: {
    headwater: 745.0,
    swe: [0, 210, 520],
    baseflow: 620,
    wetness: 0.5,
    unitsOnline: [1, 2, 3],
    initialSetpointMW: 240,
    unitsInMaintenance: [0],
  },
  weather: [
    { h: 0, tempC: 12, precipMmH: 0.04 },
    { h: 84, tempC: 13, precipMmH: 0.05 },
    { h: 168, tempC: 11, precipMmH: 0.06 },
  ],
  priceOverlays: [],
  events: [
    { id: 'E18', atH: 0, durationH: 120, target: 0 },
    { id: 'E04', atH: 14, target: 2 },
    { id: 'E05', atH: 52, durationH: 9, target: 1 },
    { id: 'E09', atH: 96, durationH: 20, params: { rate: 0.1 } },
    { id: 'E22', atH: 78, durationH: 26, params: { transducer: 0, drift: 0.25, driftRate: 0.03 } },
  ],
  hazardRate: 1.2,
  forecastBias: 0,
  forecastBiasDecayH: 0,
  objectives: [
    { id: 'reserve', text: 'Meet the reserve obligation in at least 95 % of hours', kind: 'reserve-met-fraction', value: 0.95 },
    { id: 'band', text: 'Finish inside the rule-curve band around 741 m', kind: 'hw-in-band-at-end', value: 741 },
    { id: 'env', text: 'Environmental compliance at least 98 %', kind: 'env-compliance-at-least', value: 98 },
  ],
  debriefHints: [
    'Reserve is carried by online units with headroom. When a machine trips, the shortfall is the tripped unit’s load *and* the reserve it was carrying — check both before deciding whether to start another.',
  ],
  gradeThresholds: [9_000_000, 7_500_000, 6_000_000, 3_500_000],
};

export const theLine: ScenarioConfig = {
  id: 's5-the-line',
  index: 5,
  name: 'The Line',
  siteId: SITE,
  start: '2026-06-02T00:00',
  durationH: 96,
  teaches: 'Transmission outage during high inflow: store or spill.',
  briefing:
    'Inflow is 2,200 m³/s and the pool is at 749 m. The plant can pass about 820 m³/s through the ' +
    'machines, so you are already storing.\n\n' +
    'In twenty hours the dispatcher will lose a circuit and cut your export limit to 500 MW for ' +
    'about thirty hours. You will have roughly 0.35 × 10⁹ m³ of surcharge and about 1,400 m³/s of ' +
    'water you cannot turn into electricity.\n\n' +
    'The surcharge pool is a tool. So is the phone: the flood-operations waiver takes the TDG limit ' +
    'from 110 % to 115 %, and asking for it is part of operating, not cheating.',
  initial: {
    headwater: 749.0,
    swe: [0, 260, 600],
    baseflow: 700,
    wetness: 0.5,
    unitsOnline: [0, 1, 2, 3],
    initialSetpointMW: 260,
  },
  weather: [
    { h: 0, tempC: 12.5, precipMmH: 0.05 },
    { h: 48, tempC: 13.5, precipMmH: 0.04 },
    { h: 96, tempC: 12, precipMmH: 0.05 },
  ],
  priceOverlays: [],
  events: [{ id: 'E06', atH: 20, durationH: 30, params: { factor: 0.4 } }],
  hazardRate: 1.0,
  forecastBias: 0,
  forecastBiasDecayH: 0,
  objectives: [
    { id: 'fsl', text: 'Stay below FSL, or declare flood operations', kind: 'hw-below', value: 758 },
    { id: 'env', text: 'Environmental compliance at least 90 %', kind: 'env-compliance-at-least', value: 90 },
    { id: 'safety', text: 'Safety grade B or better', kind: 'safety-at-least', value: 1 },
  ],
  debriefHints: [
    'Spilling water you could have generated costs twice: the energy you did not sell, and the gas you put in the river. The surcharge pool between 754 and 758 m holds 0.35 × 10⁹ m³ — about seventy hours of the shortfall.',
  ],
  gradeThresholds: [5_500_000, 4_000_000, 2_500_000, 0],
};

export const seep: ScenarioConfig = {
  id: 's6-seep',
  index: 6,
  name: 'Seep',
  siteId: SITE,
  start: '2026-06-18T08:00',
  durationH: 72,
  teaches: 'Piezometer rise: drawdown against downstream limits, and the EAP.',
  briefing:
    'Benign weather, pool at 752 m, inflow around 900 m³/s. Four hours from now the dam safety ' +
    'engineer will call about the toe piezometers.\n\n' +
    'They want 744 m within sixty hours. Do the arithmetic before you agree to it: eight metres near ' +
    'full pool is about 0.58 × 10⁹ m³, which over sixty hours is 2,700 m³/s of net release on top of ' +
    'inflow. The down-ramp limit allows about 100 m³/s of change per hour, and Ferrisburg goes to ' +
    'minor flood stage at 3,000 m³/s.\n\n' +
    'It is not fully achievable. Get as close as you can without putting the town under water, ' +
    'declare the EAP at the right time, and remember that the spillway sill is at 739 m — every ' +
    'metre you drop takes capacity away from you.',
  initial: {
    headwater: 752.0,
    swe: [0, 140, 430],
    baseflow: 640,
    wetness: 0.45,
    unitsOnline: [0, 1, 2, 3],
    initialSetpointMW: 240,
  },
  weather: [
    { h: 0, tempC: 13, precipMmH: 0.04 },
    { h: 36, tempC: 12, precipMmH: 0.05 },
    { h: 72, tempC: 12, precipMmH: 0.04 },
  ],
  priceOverlays: [],
  events: [
    { id: 'E13', atH: 4, durationH: 68, params: { seepageRate: 0.07, seepageCap: 7, targetPool: 744 } },
    { id: 'E24', atH: 10, durationH: 60, params: { maxPool: 748 } },
    { id: 'E12', atH: 2, durationH: 6, params: { magnitude: 4.6 } },
  ],
  hazardRate: 0.8,
  forecastBias: 0,
  forecastBiasDecayH: 0,
  objectives: [
    { id: 'drawdown', text: 'Draw the pool down to 746 m or lower', kind: 'drawdown-to', value: 746 },
    { id: 'flood', text: 'Keep Ferrisburg below minor flood stage', kind: 'gauge-below', value: 3000 },
    { id: 'safety', text: 'Safety grade B or better', kind: 'safety-at-least', value: 1 },
  ],
  debriefHints: [
    'The low-level outlets are the drawdown tool: 677 m³/s at full pool, they work at any level, and they travel at 0.50 m/min against the spillway’s 0.30. Their discharge is 125 % TDG, so the environmental penalty is the price of the safety margin — and here it is the right trade.',
  ],
  gradeThresholds: [-500_000, -2_000_000, -5_000_000, -12_000_000],
};

export const coldSnap: ScenarioConfig = {
  id: 's7-cold-snap',
  index: 7,
  name: 'Cold Snap',
  siteId: SITE,
  start: '2027-01-06T00:00',
  durationH: 168,
  teaches: 'Ice, heaters, frazil, and negative overnight prices.',
  briefing:
    'An arctic outbreak arrives tonight. Valley temperatures reach −18 °C, the morning and evening ' +
    'peaks will be extreme, and wind will push overnight prices negative.\n\n' +
    'Two traps. The gates will not move until their heaters have run for two hours, so turn them on ' +
    'before you need them. And frazil ice forms on unloaded intakes — shutting units down overnight ' +
    'to dodge a −$12 price is exactly how you arrive at the 07:00 peak with derated machines.\n\n' +
    'The winter minimum flow is 120 m³/s, which is below what four units need to stay out of the ' +
    'rough zone. This is a one-or-two-unit night by design.',
  initial: {
    headwater: 749.0,
    swe: [90, 340, 560],
    baseflow: 240,
    wetness: 0.3,
    unitsOnline: [0, 1],
    initialSetpointMW: 150,
  },
  weather: [
    { h: 0, tempC: -3, precipMmH: 0.02 },
    { h: 12, tempC: -12, precipMmH: 0.01 },
    { h: 48, tempC: -18, precipMmH: 0 },
    { h: 108, tempC: -14, precipMmH: 0.01 },
    { h: 168, tempC: -5, precipMmH: 0.03 },
  ],
  priceOverlays: [
    { fromH: 26, toH: 30, price: 180, label: 'Wednesday morning peak' },
    { fromH: 50, toH: 54, price: -12, label: 'Overnight wind surplus' },
    { fromH: 74, toH: 78, price: 210, label: 'Friday morning peak' },
    { fromH: 98, toH: 102, price: -12, label: 'Overnight wind surplus' },
  ],
  events: [
    { id: 'E27', atH: 6, durationH: 150, params: { maxDerate: 0.45 } },
    { id: 'E10', atH: 20, durationH: 120, params: { maxDerate: 0.5 } },
    { id: 'E11', atH: 12, durationH: 130 },
    { id: 'E08', atH: 50, durationH: 4, params: { price: -12 } },
  ],
  hazardRate: 1.0,
  forecastBias: 0,
  forecastBiasDecayH: 0,
  objectives: [
    { id: 'minflow', text: 'Never breach the minimum instream flow', kind: 'min-flow-held', value: 0 },
    { id: 'env', text: 'Environmental compliance at least 99 %', kind: 'env-compliance-at-least', value: 99 },
    { id: 'band', text: 'Finish inside the rule-curve band around 748 m', kind: 'hw-in-band-at-end', value: 748 },
  ],
  debriefHints: [
    'Keeping one machine loaded overnight at a negative price costs a few thousand dollars. Arriving at a $210 peak with two derated machines costs an order of magnitude more.',
  ],
  gradeThresholds: [4_500_000, 3_500_000, 2_500_000, 1_000_000],
};

export const cascade: ScenarioConfig = {
  id: 's10-cascade',
  index: 10,
  name: 'Cascade',
  siteId: SITE,
  start: '2026-05-28T00:00',
  durationH: 168,
  teaches: 'Two dams, one river.',
  briefing:
    'Kettle Narrows and Lower Sable are being operated as one river this week, and you have both ' +
    'consoles. Switch between them in the header.\n\n' +
    'Your release becomes their inflow fourteen hours later. They have a metre and a half of ' +
    'pondage to absorb whatever you send, and no way to refuse it. A steady release is worth more ' +
    'to them than an optimised one — and because their spill is your gas problem too, it is worth ' +
    'more to you as well.\n\n' +
    'Coordinate before you move.',
  initial: {
    headwater: 741.0,
    swe: [0, 300, 640],
    baseflow: 680,
    wetness: 0.5,
    unitsOnline: [0, 1, 2],
    initialSetpointMW: 220,
  },
  weather: [
    { h: 0, tempC: 11, precipMmH: 0.05 },
    { h: 84, tempC: 12.5, precipMmH: 0.04 },
    { h: 168, tempC: 11, precipMmH: 0.05 },
  ],
  priceOverlays: [],
  events: [
    { id: 'E28', atH: 0, durationH: 168 },
    { id: 'E21', atH: 40, durationH: 10, params: { flow: 500 } },
  ],
  hazardRate: 1.0,
  forecastBias: 0,
  forecastBiasDecayH: 0,
  objectives: [
    { id: 'band', text: 'Finish inside the rule-curve band around 745 m', kind: 'hw-in-band-at-end', value: 745 },
    { id: 'env', text: 'Environmental compliance at least 97 %', kind: 'env-compliance-at-least', value: 97 },
  ],
  debriefHints: [
    'A steady release is worth more to the downstream plant than an optimised one. Their whole operating range is 1.5 m.',
  ],
  gradeThresholds: [8_000_000, 6_500_000, 5_000_000, 2_500_000],
  // Both plants run in the same worker; your gauge flow is their inflow, fourteen hours later.
  cascade: { lagH: 14, downstream: cascadeDownstream },
};

export const kettleNarrowsScenarios = [
  handover,
  quietOctober,
  freeboard,
  atmosphericRiver,
  trip,
  theLine,
  seep,
  coldSnap,
  cascade,
];
